import { Component, computed, effect, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ServiceGroupsUpsertDialogStore } from './service-groups-upsert-dialog.store';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ServiceGroupObject } from '@libs/types/src/services/object-type/service/service-group.object';
import { ServiceGroupCreateInput } from '@libs/types/src/services/input-type/services/service-group.create-input';
import { ServiceGroupUpdateInput } from '@libs/types/src/services/input-type/services/service-group.update-input';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import { ColorPickerModule } from '@openng/optimus-ui/colorpicker';
import { fb, router, translate } from '../../../../../shared/shared.global-service';
import { AppFormGroup } from '../../../../../../utils/form.utils';
import { contentGlobalSuccess } from '../../../../content.error';
import { ServiceGroupEnum, serviceGroupHasValidity } from '@libs/types/src/services/enum/service-group.enum';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { dateify } from '../../../../../shared/helpers/date.helper';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilterInput } from '@it2go/data-grid';
import { TableColumn, TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { userChipField } from '../../../../../shared/components/kit/table/table.fields';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { CardTableComponent } from '../../../../../shared/components/kit/table/card-table.component';
import {
    ServiceGroupUpsertClientsDialogComponent,
} from '../service-group-upsert-clients-dialog/service-group-upsert-clients-dialog.component';
import {
    ServiceGroupUpsertOffersDialogComponent,
} from '../service-group-upsert-offers-dialog/service-group-upsert-offers-dialog.component';
import { UserChipRenderTypeEnum } from '../../../../../../components/kit/user-chip/user-chip.component.types';
import { folderUri } from '../../../../../../utils/router.utils';
import {
    ServiceGroupEditItemValidityDialogComponent,
} from '../service-group-edit-offer-dialog/service-group-edit-item-validity-dialog.component';
import { ColorInputComponent } from '../../../../../../components/kit/input/color-input/color-input.component';
import {
    ServiceGroupTypeEnum,
    serviceGroupTypeEnumOptions,
} from '@libs/types/src/services/enum/service-group-type.enum';

@Component({
    selector: 'app-service-groups-upsert-dialog',
    templateUrl: 'service-groups-upsert-dialog.component.html',
    styleUrl: 'service-groups-upsert-dialog.component.scss',
    providers: [ServiceGroupsUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextInputComponent,
        CheckInputComponent,
        ReactiveFormsModule,
        ColorPickerModule,
        DateInputComponent,
        SelectInputComponent,
        ColorInputComponent,
        CardTableComponent,
    ],
})
export class ServiceGroupsUpsertDialogComponent extends BaseDialogComponent {
    public readonly store = inject(ServiceGroupsUpsertDialogStore);
    protected readonly confirm = inject(ConfirmBuilder);
    protected readonly dialogService = inject(DialogService);

    protected serviceGroup: ServiceGroupObject | null = null;
    protected serviceId!: number;

    protected form = new AppFormGroup({
        active: fb.control<boolean>(true),
        groupType: fb.control<ServiceGroupTypeEnum>(ServiceGroupTypeEnum.GROUP, { nonNullable: true }),
        name: fb.control<string>('', Validators.required),
        description: fb.control<string>(''),
        color: fb.control<string | null>('#ff0000'),
        validFrom: fb.control<Date>(new Date()),
        validTo: fb.control<Date | null>(null),
        mpsvTargetGroupId: fb.control<number | null>(null),
    });

    typeMpsvChanged = toSignal(
        this.form.controls.mpsvTargetGroupId.valueChanges,
    );

    protected recordsTable = viewChild<CardTableComponent>(CardTableComponent);

    protected additionalFilters = computed<FilterInput[][]>(() => [
        [{
            column: 'serviceId',
            operator: 'eq',
            value: [String(this.serviceId || 0)],
        }],
        [{
            column: 'groupId',
            operator: 'eq',
            value: [String(this.serviceGroup?.id || 0)],
        }],

    ]);

    protected folderGroupFrag = `
        id
        folderId
        validFrom
        validTo
        folder {
            person {
              id
              titleBeforeName
              titleAfterName
              name
              surname
              birthdate
            }
        }
    `;

    protected offerGroupFrag = `
        id
        offerId
        validFrom
        validTo
        offer {
            name
            code
        }
    `;

    protected columns: TableColumn[] = [
        userChipField(
            'client',
            UserChipRenderTypeEnum.Client,
            {
                dataKey: 'folder.person',
                commandCallback: (item: any) => {
                    router.navigate([folderUri(item.folderId || item.id, item.serviceId)]).then();
                    this.dialogRef.close();
                },
                filterable: false,
            },
        ),
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
        },
    ];

    protected offerColumns: TableColumn[] = [
        {
            name: 'name',
            dataKey: 'offer.name',
        },
        {
            name: 'code',
            dataKey: 'offer.code',
        },
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
        },
    ];

    protected prefix = 'service.settings.group.';
    protected readonly groupTypeOptions = serviceGroupTypeEnumOptions;

    constructor() {
        super();

        this.serviceGroup = this.dialogConfig.data.serviceGroup;
        this.serviceId = this.dialogConfig.data.serviceId;

        if (this.serviceGroup) {
            this.form.patchValue({
                ...this.serviceGroup,
                validFrom: dateify(this.serviceGroup?.validFrom),
                validTo: dateify(this.serviceGroup?.validTo),
                ...JSON.parse(this.serviceGroup?.settings || '{}'),
            });
        }

        if (this.dialogConfig.data.groupType === ServiceGroupEnum.SOCIAL_TARGET) {
            this.form.controls.validFrom.setValidators(Validators.required);
        }

        effect(() => {
            if (this.typeMpsvChanged() && !this.form.controls.name.value) {
                this.form.controls.name.setValue(
                    this.store.mpsvTargetGroupScroll
                        .items()
                        .find((i) => i.id === this.typeMpsvChanged())?.name || '',
                );
            }
        });
    }

    override save(_event: Event): void | Promise<void> {
        if (isInvalid(this.form)) return;

        const _baseData = {
            active: this.form.value.active,
            groupType: this.form.value.groupType,
            name: this.form.value.name,
            description: this.form.value.description,
            color: this.form.value.color,
            validFrom: this.form.value.validFrom,
            validTo: this.form.value.validTo,
            settings: JSON.stringify({
                mpsvTargetGroupId: this.form.value.mpsvTargetGroupId,
            }),
        };

        if (!this.serviceGroup) {
            const data = _baseData as ServiceGroupCreateInput;
            data.serviceId = Number(this.serviceId);
            data.type = this.dialogConfig.data.groupType;

            this.store.api.createServiceGroup(data).then(() => {
                contentGlobalSuccess.next('common.created');
                this.dialogRef.close({ refresh: true });
            });
        } else {
            const data = _baseData as ServiceGroupUpdateInput;
            data.id = this.serviceGroup.id;

            this.store.api.updateServiceGroup(data).then(() => {
                contentGlobalSuccess.next('common.updated');
                this.dialogRef.close({ refresh: true });
            });
        }
    }

    protected deleteFolderGroup(event: any) {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deleteFolderGroup({ id: event.item.id }).then(() => {
                    contentGlobalSuccess.next('common.deleted');
                    this.recordsTable()?.reload();
                });
            },
            event,
        );
    }

    protected addFoldersGroup() {
        const ref = this.dialogService.open(
            ServiceGroupUpsertClientsDialogComponent,
            {
                header: translate.instant('service.settings.group.upsertDialog.clients.add'),
                styleClass: 'p-dialog-width-30',
                data: {
                    serviceId: this.serviceId,
                    folderIds: this.store.folderIds(),
                    groupId: this.serviceGroup?.id || 0,
                },
                modal: true,
                focusOnShow: false,
            });

        ref?.onClose.subscribe((res) => {
            if (res && res.refresh) {
                this.recordsTable()?.reload();
            }
        });
    }

    protected deleteOfferGroup(event: any) {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deleteSocialServiceOfferGroup({ id: event.item.id }).then(() => {
                    contentGlobalSuccess.next('common.deleted');
                    this.recordsTable()?.reload();
                });
            },
            event,
        );
    }

    protected addOffersGroup() {
        const ref = this.dialogService.open(
            ServiceGroupUpsertOffersDialogComponent,
            {
                header: translate.instant('service.settings.group.upsertDialog.offers.add'),
                styleClass: 'p-dialog-width-30',
                data: {
                    serviceId: this.serviceId,
                    groupId: this.serviceGroup?.id || 0,
                },
                modal: true,
                focusOnShow: false,
            },
        );

        ref?.onClose.subscribe((res) => {
            if (res && res.refresh) {
                this.recordsTable()?.reload();
            }
        });
    }

    protected editItemValidity(event: any, isOffer = false) {
        const ref = this.dialogService.open(
            ServiceGroupEditItemValidityDialogComponent,
            {
                header: translate.instant(
                    isOffer
                        ? 'service.settings.group.upsertDialog.offers.edit'
                        : 'service.settings.group.upsertDialog.clients.edit'),
                styleClass: 'p-dialog-width-30',
                data: {
                    item: event,
                    promise: isOffer
                        ? this.store.api.updateSocialServiceOfferGroup
                        : this.store.api.updateFolderGroup,
                },
                modal: true,
                focusOnShow: false,
            },
        );

        ref?.onClose.subscribe((res) => {
            if (res && res.refresh) {
                this.recordsTable()?.reload();
            }
        });
    }

    protected readonly ServiceGroupEnum = ServiceGroupEnum;
    protected readonly serviceGroupHasValidity = serviceGroupHasValidity;
}
